import { useQuery } from '@apollo/client';
import { GET_ALL_PRICING_TABLES } from '@gql/pricingTable';
import { usePageSize } from '@hooks/usePageSize';
import {
    Add as AddIcon,
    ContentCopy as CopyIcon,
    Visibility as ViewIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PricingTable } from '../../../types/pricingTable';

interface GetAllPricingTablesData {
    getAllPricingTables: PricingTable[];
}

const PricingTableListModule: React.FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const { pageSize: rowsPerPage, storePageSize } = usePageSize({ storageKey: 'pricing-tables-page-size' });
    // const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedTable, setSelectedTable] = useState<PricingTable | null>(null);

    const { data, loading, error } = useQuery<GetAllPricingTablesData>(GET_ALL_PRICING_TABLES, {
        errorPolicy: 'all',
    });

    // const [deletePricingTable, { loading: deleteLoading }] = useMutation(DELETE_PRICING_TABLE, {
    //     onCompleted: () => {
    //         setDeleteDialogOpen(false);
    //         setSelectedTable(null);
    //         refetch();
    //     },
    //     onError: (error) => {
    //         console.error('Error deleting pricing table:', error);
    //     },
    // });

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        storePageSize(parseInt(event.target.value, 10));
        setPage(0);
    };

    // const handleDelete = (table: PricingTable) => {
    //     setSelectedTable(table);
    //     setDeleteDialogOpen(true);
    // };

    // const confirmDelete = () => {
    //     if (selectedTable) {
    //         deletePricingTable({
    //             variables: { _id: selectedTable._id },
    //         });
    //     }
    // };

    const handleCopyId = async (id: string) => {
        try {
            await navigator.clipboard.writeText(id);
            // You might want to show a toast notification here
        } catch (err) {
            console.error('Failed to copy ID:', err);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    Error loading pricing tables: {error.message}
                </Alert>
            </Box>
        );
    }

    const pricingTables = data?.getAllPricingTables || [];
    const paginatedTables = pricingTables.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box p={3} minHeight={'calc(100vh - 118px)'}>
            {/* Header */}
            {/* <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/pricing-tables/create')}
                    sx={{ backgroundColor: '#1976d2' }}>
                    Create New Table
                </Button>
            </Box> */}

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Table ID</TableCell>
                            <TableCell>Table Name</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="center">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedTables.map((table) => (
                            <TableRow key={table._id} hover>
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.8rem',
                                                maxWidth: 150,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                            }}>
                                            {table._id}
                                        </Typography>
                                        <Tooltip title="Copy ID">
                                            <IconButton size="small" onClick={() => handleCopyId(table._id)}>
                                                <CopyIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="subtitle2" fontWeight="bold">
                                        {table.tableName}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            maxWidth: 300,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {table.tableDescription || 'No description'}
                                    </Typography>
                                </TableCell>
                                <TableCell align="center">
                                    <Box display="flex" justifyContent="center" gap={1}>
                                        <Tooltip title="View Details">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/pricing-tables/detail/${table._id}`)}>
                                                <ViewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {/* <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                onClick={() => navigate(`/pricing-tables/edit/${table._id}`)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete">
                                            <IconButton
                                                size="small"
                                                color="error"
                                                onClick={() => handleDelete(table)}>
                                                <DeleteIcon />
                                            </IconButton>
                                        </Tooltip> */}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                        {paginatedTables.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No pricing tables found
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={() => navigate('/pricing-tables/create')}
                                        sx={{ mt: 2 }}>
                                        Create First Table
                                    </Button>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={pricingTables.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </TableContainer>

            {/* Delete Confirmation Dialog */}
            {/* <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Pricing Table</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete the pricing table "{selectedTable?.tableName}"? This action
                        cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button
                        onClick={confirmDelete}
                        color="error"
                        variant="contained"
                        disabled={deleteLoading}
                        startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog> */}
        </Box>
    );
};

export default PricingTableListModule;
